import { motion } from "framer-motion";

function toNumber(value) {
    if (value === undefined || value === null || value === "") return 0;
    const normalized = typeof value === "string" ? value.replace("%", "").replace(",", ".") : value;
    const numeric = Number(normalized);
    return Number.isFinite(numeric) ? numeric : 0;
}

function pickFirstValue(source, keys) {
    if (!source) return undefined;
    const keyList = Array.isArray(keys) ? keys : [keys];
    const key = keyList.find((item) => source[item] !== undefined && source[item] !== null);
    return key ? source[key] : undefined;
}

function pickValue(source, keys, fallback = 0) {
    const value = pickFirstValue(source, keys);
    return value !== undefined && value !== null ? value : fallback;
}

function buildPercent(made, attempted) {
    const madeValue = toNumber(made);
    const attemptedValue = toNumber(attempted);

    if (!attemptedValue) return 0;
    return (madeValue / attemptedValue) * 100;
}

function getMetricValue(source, metric) {
    const direct = pickFirstValue(source, metric.keys || []);
    if (direct !== undefined && direct !== null && direct !== "") {
        return direct;
    }

    if (metric.percentFrom) {
        const made = pickValue(source, metric.percentFrom.madeKeys, 0);
        const attempted = pickValue(source, metric.percentFrom.attemptKeys, 0);
        return buildPercent(made, attempted);
    }

    return 0;
}

function formatValue(value, metric) {
    if (metric.isPercent) {
        return `${toNumber(value).toFixed(1)}%`;
    }

    if (metric.decimals !== undefined) {
        return toNumber(value).toFixed(metric.decimals);
    }

    return toNumber(value).toLocaleString("es-UY");
}

function getJugadorSource(profile, scope, mode) {
    if (!profile?.jugadorProfile) {
        return null;
    }

    if (scope === "carrera") {
        return mode === "promedios"
            ? profile.jugadorProfile.carreraProm
            : profile.jugadorProfile.carrera;
    }

    if (mode === "promedios") {
        const selectedPromedio = (profile.jugadorProfile.torneosProm || []).find((item) => item?.liga === scope);
        return selectedPromedio || (profile.jugadorProfile.torneosProm || [])[0] || null;
    }

    const selectedTotal = (profile.jugadorProfile.torneos || []).find((item) => item?.liga === scope);
    return selectedTotal || (profile.jugadorProfile.torneos || [])[0] || null;
}

function getTecnicoSource(profile, scope) {
    if (!profile?.tecnicoProfile) {
        return null;
    }

    if (scope === "carrera") {
        return profile.tecnicoProfile.carrera;
    }

    const selectedTorneo = (profile.tecnicoProfile.torneos || []).find((item) => item?.liga === scope);
    return selectedTorneo || (profile.tecnicoProfile.torneos || [])[0] || null;
}

const jugadorPromedioMetrics = [
    { id: "min", label: "Minutos", keys: ["mpg"], decimals: 1 },
    { id: "pts", label: "Puntos", keys: ["ppg"], decimals: 1 },
    { id: "tth", label: "Triples anotados", keys: ["tthpg", "triplesHechosPg"], decimals: 1 },
    {
        id: "ttp",
        label: "Triples %",
        keys: ["ttperc", "triplesPercent"],
        isPercent: true,
        percentFrom: {
            madeKeys: ["tthpg", "triplesHechosPg"],
            attemptKeys: ["ttlpg", "triplesIntentPg"],
        },
    },
    { id: "tdh", label: "Dobles anotados", keys: ["tdhpg", "doblesHechosPg"], decimals: 1 },
    {
        id: "tdp",
        label: "Dobles %",
        keys: ["tdperc", "doblesPercent"],
        isPercent: true,
        percentFrom: {
            madeKeys: ["tdhpg", "doblesHechosPg"],
            attemptKeys: ["tdlpg", "doblesIntentPg"],
        },
    },
    { id: "tlh", label: "Libres anotados", keys: ["tlhpg", "libresHechosPg"], decimals: 1 },
    {
        id: "tlp",
        label: "Libres %",
        keys: ["tlperc", "libresPercent"],
        isPercent: true,
        percentFrom: {
            madeKeys: ["tlhpg", "libresHechosPg"],
            attemptKeys: ["tllpg", "libresIntentPg"],
        },
    },
    { id: "ro", label: "Rebotes ofensivos", keys: ["ropg", "orpg", "rop"], decimals: 1 },
    { id: "rd", label: "Rebotes defensivos", keys: ["rdpg", "drpg", "rdp"], decimals: 1 },
    { id: "rt", label: "Rebotes totales", keys: ["rtpg", "trpg"], decimals: 1 },
    { id: "ast", label: "Asistencias", keys: ["apg", "astpg"], decimals: 1 },
    { id: "rob", label: "Robos", keys: ["spg", "robpg", "rpg", "stlpg"], decimals: 1 },
    { id: "tap", label: "Tapas", keys: ["tapg", "blqpg", "bpg"], decimals: 1 },
];

const jugadorTotalMetrics = [
    { id: "partidos", label: "Partidos jugados", keys: ["partidos"], decimals: 0 },
    { id: "ganados", label: "Partidos ganados", keys: ["ganados"], decimals: 0 },
    { id: "perdidos", label: "Partidos perdidos", keys: ["perdidos", "pp"], decimals: 0, lowerIsBetter: true },
    { id: "win", label: "% ganados", keys: ["winPerc"], isPercent: true },
    { id: "min", label: "Minutos", keys: ["min"], decimals: 0 },
    { id: "pts", label: "Puntos", keys: ["pts"], decimals: 0 },
    { id: "tth", label: "Triples anotados", keys: ["tth", "triplesHechos"], decimals: 0 },
    {
        id: "ttp",
        label: "Triples %",
        keys: ["ttperc", "triplesPercent"],
        isPercent: true,
        percentFrom: {
            madeKeys: ["tth", "triplesHechos"],
            attemptKeys: ["ttl", "triplesIntent"],
        },
    },
    { id: "tdh", label: "Dobles anotados", keys: ["tdh", "doblesHechos"], decimals: 0 },
    {
        id: "tdp",
        label: "Dobles %",
        keys: ["tdperc", "doblesPercent"],
        isPercent: true,
        percentFrom: {
            madeKeys: ["tdh", "doblesHechos"],
            attemptKeys: ["tdl", "doblesIntent"],
        },
    },
    { id: "tlh", label: "Libres anotados", keys: ["tlh", "libresHechos"], decimals: 0 },
    {
        id: "tlp",
        label: "Libres %",
        keys: ["tlperc", "libresPercent"],
        isPercent: true,
        percentFrom: {
            madeKeys: ["tlh", "libresHechos"],
            attemptKeys: ["tll", "libresIntent"],
        },
    },
    { id: "ro", label: "Rebotes ofensivos", keys: ["ro"], decimals: 0 },
    { id: "rd", label: "Rebotes defensivos", keys: ["rd"], decimals: 0 },
    { id: "rt", label: "Rebotes totales", keys: ["rt"], decimals: 0 },
    { id: "ast", label: "Asistencias", keys: ["ast"], decimals: 0 },
    { id: "rob", label: "Robos", keys: ["rob", "stl"], decimals: 0 },
    { id: "tap", label: "Tapas", keys: ["bls", "blq", "tap"], decimals: 0 },
];

const tecnicoTotalMetrics = [
    { id: "partidos", label: "Partidos jugados", keys: ["partidos"], decimals: 0 },
    { id: "ganados", label: "Partidos ganados", keys: ["ganados"], decimals: 0 },
    { id: "perdidos", label: "Partidos perdidos", keys: ["perdidos", "pp"], decimals: 0, lowerIsBetter: true },
    { id: "win", label: "% ganados", keys: ["winPerc"], isPercent: true },
];

function getRows({ leftProfile, rightProfile, leftRole, rightRole, leftScope, rightScope, mode }) {
    const leftSource = leftRole === "tecnico"
        ? getTecnicoSource(leftProfile, leftScope)
        : getJugadorSource(leftProfile, leftScope, mode);

    const rightSource = rightRole === "tecnico"
        ? getTecnicoSource(rightProfile, rightScope)
        : getJugadorSource(rightProfile, rightScope, mode);

    const bothTecnicos = leftRole === "tecnico" && rightRole === "tecnico";
    const bothJugadores = leftRole === "jugador" && rightRole === "jugador";

    const metrics = bothTecnicos
        ? tecnicoTotalMetrics
        : bothJugadores
            ? (mode === "promedios" ? jugadorPromedioMetrics : jugadorTotalMetrics)
            : tecnicoTotalMetrics;

    return metrics.map((metric) => {
        const leftValue = getMetricValue(leftSource, metric);
        const rightValue = getMetricValue(rightSource, metric);
        const leftNumeric = toNumber(leftValue);
        const rightNumeric = toNumber(rightValue);
        const combined = leftNumeric + rightNumeric;

        return {
            id: metric.id,
            label: metric.label,
            leftValue,
            rightValue,
            leftWidth: combined > 0 ? `${(leftNumeric / combined) * 100}%` : "0%",
            rightWidth: combined > 0 ? `${(rightNumeric / combined) * 100}%` : "0%",
            metric,
        };
    });
}

function ChampionBadge({ count }) {
    if (count <= 0) return <div className="h-6 sm:h-7" />;
    
    const label = count > 1 ? `Campeón x${count}` : "Campeón";
    
    return (
        <div className="rounded-md bg-yellow-300 px-2 py-0.5 text-[10px] sm:text-xs font-black text-[#2d2211] uppercase tracking-wide inline-block">
            {label}
        </div>
    );
}

function Row({ row }) {
    const lowerIsBetter = row.metric?.lowerIsBetter;
    const leftHigher = toNumber(row.leftValue) > toNumber(row.rightValue);
    const rightHigher = toNumber(row.rightValue) > toNumber(row.leftValue);
    const isTie = toNumber(row.leftValue) === toNumber(row.rightValue);
    const leftBest = lowerIsBetter ? !leftHigher && !isTie : leftHigher;
    const rightBest = lowerIsBetter ? !rightHigher && !isTie : rightHigher;

    const leftValueClass = isTie ? "text-slate-300" : leftBest ? "text-green-400" : "text-red-400";
    const rightValueClass = isTie ? "text-slate-300" : rightBest ? "text-green-400" : "text-red-400";
    const leftBarClass = isTie ? "bg-slate-400" : leftBest ? "bg-green-500" : "bg-red-500";
    const rightBarClass = isTie ? "bg-slate-400" : rightBest ? "bg-green-500" : "bg-red-500";

    return (
        <div className="grid grid-cols-12 items-center gap-2 sm:gap-3 md:gap-5">
            <div className="col-span-4 sm:col-span-5 text-right">
                <p className={`text-base sm:text-xl md:text-2xl font-black ${leftValueClass}`}>
                    {formatValue(row.leftValue, row.metric)}
                </p>
                <div className="mt-1.5 sm:mt-2 h-1.5 sm:h-2 rounded-full bg-white/10 overflow-hidden">
                    <div className={`h-full rounded-full ml-auto ${leftBarClass}`} style={{ width: row.leftWidth }} />
                </div>
            </div>

            <div className="col-span-4 sm:col-span-2 text-center">
                <span className="inline-flex px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded bg-white/5 text-[9px] sm:text-xs font-bold uppercase tracking-[0.12em] sm:tracking-[0.15em] text-slate-400 break-words">
                    {row.label}
                </span>
            </div>

            <div className="col-span-4 sm:col-span-5 text-left">
                <p className={`text-base sm:text-xl md:text-2xl font-black ${rightValueClass}`}>
                    {formatValue(row.rightValue, row.metric)}
                </p>
                <div className="mt-1.5 sm:mt-2 h-1.5 sm:h-2 rounded-full bg-white/10 overflow-hidden">
                    <div className={`h-full rounded-full ${rightBarClass}`} style={{ width: row.rightWidth }} />
                </div>
            </div>
        </div>
    );
}

export default function CompararStatsSection({
    leftProfile,
    rightProfile,
    leftRole,
    rightRole,
    leftScope,
    rightScope,
    mode,
    setMode,
    canUsePromedios,
}) {
    const rows = getRows({
        leftProfile,
        rightProfile,
        leftRole,
        rightRole,
        leftScope,
        rightScope,
        mode,
    });
    const showModeToggle = leftRole === "jugador" && rightRole === "jugador";
    
    const leftActiveProfile = leftRole === "tecnico" ? leftProfile?.tecnicoProfile : leftProfile?.jugadorProfile;
    const rightActiveProfile = rightRole === "tecnico" ? rightProfile?.tecnicoProfile : rightProfile?.jugadorProfile;
    
    const leftCampeonatos = leftActiveProfile?.campeon || [];
    const leftCampeonCount = Array.isArray(leftCampeonatos) ? leftCampeonatos.length : 0;
    
    const rightCampeonatos = rightActiveProfile?.campeon || [];
    const rightCampeonCount = Array.isArray(rightCampeonatos) ? rightCampeonatos.length : 0;

    return (
        <section className="rounded-2xl border border-white/10 bg-[#102216]/70 p-3 sm:p-5 md:p-8">
            <div className="mb-4 sm:mb-6 flex flex-col items-center gap-2 sm:gap-4">
                <h2 className="text-center text-xl sm:text-2xl font-black uppercase tracking-tight text-white">Estadísticas comparativas</h2>

                {showModeToggle ? (
                    <div className="inline-flex mx-auto p-1 rounded-xl border border-white/10 bg-[#0f1f15] shadow-2xl">
                        <button
                            type="button"
                            onClick={() => setMode?.("promedios")}
                            disabled={!canUsePromedios}
                            className={`relative z-10 px-4 md:px-6 py-2.5 text-xs md:text-sm font-bold uppercase tracking-wide rounded-lg transition-colors duration-200 ${
                                mode === "promedios"
                                    ? "text-[#062a1a]"
                                    : "text-slate-400 hover:text-white"
                            } disabled:opacity-50 disabled:hover:text-slate-400`}
                        >
                            {mode === "promedios" ? (
                                <motion.span
                                    layoutId="comparar-mode-toggle-pill"
                                    transition={{ type: "spring", stiffness: 420, damping: 34 }}
                                    className="absolute inset-0 -z-10 rounded-lg bg-green-500"
                                />
                            ) : null}
                            Promedios
                        </button>
                        <button
                            type="button"
                            onClick={() => setMode?.("totales")}
                            className={`relative z-10 px-4 md:px-6 py-2.5 text-xs md:text-sm font-bold uppercase tracking-wide rounded-lg transition-colors duration-200 ${
                                mode === "totales"
                                    ? "text-[#062a1a]"
                                    : "text-slate-400 hover:text-white"
                            }`}
                        >
                            {mode === "totales" ? (
                                <motion.span
                                    layoutId="comparar-mode-toggle-pill"
                                    transition={{ type: "spring", stiffness: 420, damping: 34 }}
                                    className="absolute inset-0 -z-10 rounded-lg bg-green-500"
                                />
                            ) : null}
                            Totales
                        </button>
                    </div>
                ) : null}
            </div>

            <div className="mb-6 sm:mb-8 grid grid-cols-12 items-center gap-2 sm:gap-3 md:gap-5">
                <div className="col-span-4 sm:col-span-5 flex justify-end">
                    <ChampionBadge count={leftCampeonCount} />
                </div>
                <div className="col-span-4 sm:col-span-2" />
                <div className="col-span-4 sm:col-span-5 flex justify-start">
                    <ChampionBadge count={rightCampeonCount} />
                </div>
            </div>

            <div className="space-y-4 sm:space-y-6">
                {rows.map((row) => (
                    <Row key={row.id} row={row} />
                ))}
            </div>
        </section>
    );
}